import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download,
  LogOut,
  Lock,
  ShieldCheck,
  Mail,
  Phone,
  Building2,
  Loader2,
  Inbox,
  FileText,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PiSymbol from "@/components/PiSymbol";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "pie_admin_token";

// Use sessionStorage (cleared on tab close) instead of localStorage so the admin
// token doesn't persist across browser sessions. Note: this still exposes the
// token to JS. A future iteration should migrate to httpOnly cookie + signed JWT.
const tokenStore = {
  get: () => sessionStorage.getItem(STORE_KEY) || "",
  set: (v) => sessionStorage.setItem(STORE_KEY, v),
  clear: () => sessionStorage.removeItem(STORE_KEY),
};

export default function Admin() {
  const [token, setToken] = useState(() => tokenStore.get());
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const logout = useCallback(() => {
    tokenStore.clear();
    setToken("");
    setItems([]);
    setSelected(null);
  }, []);

  const loadItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Token": token },
      });
      setItems(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) loadItems();
  }, [token, loadItems]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return toast.error("Enter password");
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      tokenStore.set(res.data.token);
      setToken(res.data.token);
      toast.success("Logged in");
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoginLoading(false);
    }
  };

  const downloadFile = (id, filename) => {
    const url = `${API}/admin/submissions/${id}/file?token=${encodeURIComponent(token)}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "bom-file";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center pt-24 pb-16 px-6">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="glass-card p-8 md:p-10 w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-6">
            <PiSymbol size={42} />
            <div>
              <div className="section-label">Admin Console</div>
              <div className="font-display text-2xl font-bold mt-1">
                BOM Submissions
              </div>
            </div>
          </div>
          <Label className="font-display uppercase tracking-[0.18em] text-xs text-white/70 block mb-2">
            Admin Password
          </Label>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="bg-[#0f0f0f] border-white/15 text-white focus-visible:ring-[#39FF14] pl-9"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loginLoading}
            className="btn-primary w-full mt-6 disabled:opacity-60"
          >
            {loginLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ShieldCheck size={16} />
            )}
            {loginLoading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-[11px] text-white/40 mt-5 text-center">
            Restricted area. Authorised personnel only.
          </p>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="pt-28 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="section-label">Admin Console</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-2">
              BOM <span className="text-[#39FF14]">Enquiries</span>
            </h1>
            <p className="text-sm text-white/60 mt-2">
              {items.length} submission{items.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="btn-ghost text-xs"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/60">
            <Loader2 className="animate-spin" size={18} /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Inbox size={36} className="text-[#39FF14] mx-auto mb-4" />
            <div className="font-display text-xl">No submissions yet.</div>
            <p className="text-sm text-white/55 mt-2">
              New BOM enquiries from the contact form will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1 space-y-3 max-h-[80vh] overflow-y-auto pr-2">
              {items.map((it) => {
                const active = selected?.id === it.id;
                return (
                  <button
                    key={it.id}
                    data-testid={`admin-row-${it.id}`}
                    onClick={() => setSelected(it)}
                    className={`text-left w-full glass-card p-4 transition-all ${
                      active ? "border-[#39FF14]/70 bg-[#39FF14]/5" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-display font-semibold text-sm truncate">
                        {it.company_name}
                      </div>
                      {it.file_storage_path && (
                        <FileText size={12} className="text-[#39FF14]" />
                      )}
                    </div>
                    <div className="text-xs text-white/55 truncate">
                      {it.full_name} • {it.office}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#39FF14] mt-1.5 font-display">
                      {it.query_type}
                    </div>
                    <div className="text-[10px] text-white/35 mt-1">
                      {new Date(it.created_at).toLocaleString("en-IN")}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-2">
              {selected ? (
                <div
                  data-testid="admin-detail"
                  className="glass-card p-7 sticky top-24"
                >
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-display">
                        Submission
                      </div>
                      <h2 className="font-display text-2xl font-bold mt-1">
                        {selected.company_name}
                      </h2>
                      <div className="text-xs text-white/50 mt-1">
                        {new Date(selected.created_at).toLocaleString("en-IN")}
                      </div>
                    </div>
                    {selected.file_storage_path && (
                      <button
                        onClick={() =>
                          downloadFile(selected.id, selected.file_name)
                        }
                        data-testid="admin-download-file"
                        className="btn-primary text-xs"
                      >
                        <Download size={14} />
                        {selected.file_name || "BOM file"}
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Detail label="Full Name" value={selected.full_name} />
                    <Detail
                      label="Designation"
                      value={selected.designation || "—"}
                    />
                    <Detail label="Email" value={selected.email} icon={Mail} />
                    <Detail label="Phone" value={selected.phone} icon={Phone} />
                    <Detail
                      label="Office"
                      value={selected.office}
                      icon={Building2}
                    />
                    <Detail label="Query Type" value={selected.query_type} />
                  </div>

                  <div className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-display text-white/45 mb-2">
                      Categories
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(selected.categories || []).length === 0 && (
                        <span className="text-sm text-white/55">—</span>
                      )}
                      {(selected.categories || []).map((c) => (
                        <span
                          key={c}
                          className="px-3 py-1 rounded-full border border-[#39FF14]/40 text-xs font-display uppercase tracking-[0.18em] text-[#39FF14]"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-display text-white/45 mb-2">
                      Message
                    </div>
                    <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
                      {selected.message || "—"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="glass-card p-12 text-center text-white/55">
                  Select a submission to view full details.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function Detail({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] font-display text-white/45 mb-1">
        {label}
      </div>
      <div className="text-sm text-white/85 flex items-center gap-2">
        {Icon && <Icon size={12} className="text-[#39FF14]" />}
        {value}
      </div>
    </div>
  );
}
